import React, { createContext, useContext, useRef } from "react";
import axios from "axios";
import { HoldingsInformation } from "../dto/HoldingsInformation";
import {
  ItemAvailability,
  ItemAvailabilityReturn,
} from "../components/pages/resource/ItemAvailability";

const TTL = 5 * 60 * 1000;
const BATCH_WINDOW = 200;

type CacheEntry<T> = { data: T; timestamp: number };
type AvailabilityCache = Map<string, CacheEntry<ItemAvailability>>;
type InFlightMap = Map<string, Promise<ItemAvailability>>;

type BatchRequest = {
  key: string;
  resourceId: number;
  barcode: string;
  resolve: (data: ItemAvailability) => void;
  reject: (err: unknown) => void;
};

interface AvailabilityContextType {
  getAvailabilityForHoldings: (
    resourceId: number,
    holdings: HoldingsInformation[]
  ) => Promise<ItemAvailability[]>;
}

const AvailabilityContext = createContext<AvailabilityContextType | null>(null);

export const useAvailability = () => {
  const ctx = useContext(AvailabilityContext);
  if (!ctx) throw new Error("useAvailability must be used inside its Provider");
  return ctx;
};

export const AvailabilityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const cacheRef = useRef<AvailabilityCache>(new Map());
  const inFlightRef = useRef<InFlightMap>(new Map());
  const batchQueueRef = useRef<BatchRequest[]>([]);
  const batchTimerRef = useRef<number | null>(null);

  const queueAvailability = (resourceId: number, barcode: string) => {
    const key = `${resourceId}:${barcode}`;
    const now = Date.now();

    const cached = cacheRef.current.get(key);
    if (cached && now - cached.timestamp < TTL) {
      return Promise.resolve(cached.data);
    }

    if (inFlightRef.current.has(key)) {
      return inFlightRef.current.get(key)!;
    }

    const promise = new Promise<ItemAvailability>((resolve, reject) => {
      batchQueueRef.current.push({ key, resourceId, barcode, resolve, reject });
    });

    inFlightRef.current.set(key, promise);

    if (!batchTimerRef.current) {
      batchTimerRef.current = window.setTimeout(flushBatch, BATCH_WINDOW);
    }

    return promise;
  };

  const flushBatch = async () => {
    const batch = [...batchQueueRef.current];
    batchQueueRef.current = [];
    batchTimerRef.current = null;

    const params = batch.map(({ resourceId, barcode }) => ({
      resourceId,
      itemIdentifier: barcode,
    }));

    try {
      const currentTimestamp = new Date().getTime();
      const { data } = await axios.post<ItemAvailabilityReturn>(
        `/availability?_=${currentTimestamp}`,
        params
      );
      const now = Date.now();

      // create a lookup map from response
      const lookup = new Map<string, ItemAvailability>();
      data.itemAvailabilities.forEach((item) => {
        const key = `${item.resourceId}:${item.itemIdentifier}`;
        lookup.set(key, item);
      });

      batch.forEach(({ key, resolve }) => {
        const value = lookup.get(key);
        if (value) {
          cacheRef.current.set(key, { data: value, timestamp: now });
          inFlightRef.current.delete(key);
          resolve(value);
        } else {
          // fallback if somehow not found
          inFlightRef.current.delete(key);
          resolve(null);
        }
      });
    } catch (err) {
      batch.forEach(({ key, reject }) => {
        inFlightRef.current.delete(key);
        reject(err);
      });
    }
  };

  const getAvailabilityForHoldings = async (
    resourceId: number,
    holdings: HoldingsInformation[]
  ) => {
    const results: ItemAvailability[] = [];

    await Promise.all(
      holdings.map((h) =>
        queueAvailability(resourceId, h.barcode).then((value) => {
          if (value) results.push(value);
        })
      )
    );

    return results;
  };

  return (
    <AvailabilityContext.Provider value={{ getAvailabilityForHoldings }}>
      {children}
    </AvailabilityContext.Provider>
  );
};
